import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  addUsersToRole,
  searchUsers,
  fetchSecurityRoleById,
} from "../../services/securityRoleService";
import Toast from "../ui/toasts/Toast";
import BusinessUnitDropdown from "../shared/asyncDropdowns/BusinessUnitDropdown";
import DepartmentDropdown from "../shared/asyncDropdowns/DepartmentDropdown";
import DivisionDropdown from "../shared/asyncDropdowns/DivisionDropdown";
import PayGroupDropdown from "../shared/asyncDropdowns/PayGroupDropdown";
import RegionDropdown from "../shared/asyncDropdowns/RegionDropdown";
import LocationDropdown from "../shared/asyncDropdowns/LocationDropdown";
import Spinner from "../ui/spinners/Spinner";
import styles from "./security-roles.module.scss";
import { useAuthContext } from "../../store/Contexts";
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const SortDirections = {
  ASCENDING: "asc",
  DESCENDING: "desc",
};

const AddUserToRole = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [searchCriteria, setSearchCriteria] = useState({
    lastName: "",
    firstName: "",
    uniqueId: "",
    departmentIds: "",
    regionIds: "",
    payGroupIds: "",
    businessUnitIds: "",
    divisionIds: "",
  });

  const [potentialUsers, setPotentialUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [isSearchSent, setIsSearchSent] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "lastName",
    direction: SortDirections.ASCENDING,
  });
  const [isLoading, setIsLoading] = useState(false);
  const selectAllRef = useRef(null);

  const [roleDetails, setRoleDetails] = useState({
    name: "",
    description: "",
    attributes: [],
  });

  const { user } = useAuthContext();

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedUsers.length > 0 &&
        selectedUsers.length < potentialUsers.length;
    }
  }, [selectedUsers, potentialUsers]);

  useEffect(() => {
    const loadRoleDetails = async () => {
      try {
        if (roleId) {
          const fetchedRole = await fetchSecurityRoleById(parseInt(roleId, 10));
          setRoleDetails(fetchedRole);
        }
      } catch (error) {
        console.error("Failed to load role Data", error);
        Toast.error(getText('msg_e7a7d6c6cbfa'));
      } finally {
        setIsLoading(false);
      }
    };

    loadRoleDetails();
  }, [roleId, navigate]);

  const handleSelectAllRef = useCallback(
    (node) => {
      if (node) {
        node.indeterminate =
          selectedUsers.length > 0 &&
          selectedUsers.length < potentialUsers.length;
      }
    },
    [selectedUsers, potentialUsers]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleDropdownChange = (value, fieldName) => {
    setSearchCriteria((prevState) => ({ ...prevState, [fieldName]: value }));
  };

  const handleOnKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setIsSearchSent(true);

      const cleanedSearchCriteria = {};

      // only include the properties that have a value
      for (const [key, value] of Object.entries(searchCriteria)) {
        if (searchCriteria[key]) cleanedSearchCriteria[key] = value;
      }

      const results = await searchUsers(
        cleanedSearchCriteria,
        roleId
      );
      setPotentialUsers(results);
      setSelectedUsers([]);
      setSelectAllChecked(false);
    } catch (error) {
      console.error("Failed to search for users:", error);
      Toast.error(getText('msg_d5aa7d5b97ad'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key &&
          prevConfig.direction === SortDirections.ASCENDING
          ? SortDirections.DESCENDING
          : SortDirections.ASCENDING,
    }));
  };

  const handleSelectAllChange = (isChecked) => {
    setSelectAllChecked(isChecked);
    setSelectedUsers(
      isChecked ? potentialUsers.map((user) => user.userId) : []
    );
  };

  const handleUserSelectionChange = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const sortedUsers = useMemo(() => {
    return [...potentialUsers].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key])
        return sortConfig.direction === SortDirections.ASCENDING ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key])
        return sortConfig.direction === SortDirections.ASCENDING ? 1 : -1;
      return 0;
    });
  }, [potentialUsers, sortConfig]);

  const handleSave = async () => {
    if (!potentialUsers.length) {
      Toast.error(getText('msg_aa5cf025cca7'));
      return;
    }

    try {
      await addUsersToRole(roleId, selectedUsers);
      Toast.success(getText('msg_593ac0105fc7'));
      navigate(ROUTE_BUILDERS.securityRolesView(roleId));
    } catch (error) {
      console.error("Failed to add users to role:", error);
      Toast.error(getText('msg_1bf7026ceeec'));
    }
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return (
      <span className={`px-2 ${styles.sortArrowIcon}`}>
        <svg
          width="11"
          height="6"
          viewBox="0 0 11 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d={
              sortConfig.direction === SortDirections.ASCENDING
                ? "M1 5.25L5.25 0.75L9.5 5.25"
                : "M1 0.75L5.25 5.25L9.5 0.75"
            }
            stroke="#2F2B43"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className={`page-content`}>
      <div className="card">
        <div className="card-body px-0 py-4 b">
          <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
            <p className="page-title float-start card-title ms-4 mb-0">
              {`Users in Role: 
              ${isLoading ? "..." : `${roleDetails.name}`}
              `}
            </p>

            <div className="float-end d-flex align-items-center mx-4">
              <span
                type="button"
                onClick={handleCancel}
                className="ing-text-buttons mx-3"
              >
                <i className="bi bi-x me-2" />
                {getText('msg_bbfa773e5a63')}
              </span>

              <span
                type="button"
                onClick={handleSave}
                className="ing-text-buttons mx-3"
              >
                <i className="bi bi-check-lg me-2" />
                {getText('msg_efc007a393f6')}
              </span>
            </div>
          </div>
          <section className="card-title m-4 d-flex flex-wrap mb-5 overflow-visible">
            <div
              className={`form-group col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2 mb-3`}
            >
              <label htmlFor="lastName">{getText('msg_3d9fa6353df3')}</label>
              <input
                id="lastName"
                type="text"
                maxLength="50"
                className={`form-control`}
                value={searchCriteria.lastName}
                onChange={handleChange}
                onKeyDown={handleOnKeyDown}
                name="lastName"
              />
              <small className="form-text text-muted">
                {getText('msg_971d6ded4edf')} {searchCriteria?.lastName?.length}{getText('msg_2fd301945bd1')}
              </small>
            </div>

            <div
              className={`form-group col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2 mb-3`}
            >
              <label htmlFor="firstName">{getText('msg_e9a1010cf36f')}</label>
              <input
                id="firstName"
                type="text"
                maxLength="50"
                className={`form-control`}
                value={searchCriteria.firstName}
                onChange={handleChange}
                onKeyDown={handleOnKeyDown}
                name="firstName"
              />
              <small className="form-text text-muted">
                {getText('msg_971d6ded4edf')} {searchCriteria?.firstName?.length}{getText('msg_2fd301945bd1')}
              </small>
            </div>

            {user.jwt?.ind != null && (
              <>
                <div
                  className={`form-group col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2 mb-3`}
                >
                  <label htmlFor="uniqueId">{getText('msg_96f2d6768c71')}</label>
                  <input
                    id="uniqueId"
                    type="text"
                    maxLength="50"
                    className={`form-control`}
                    value={searchCriteria.uniqueId}
                    onChange={handleChange}
                    name="uniqueId"
                  />
                  <small className="form-text text-muted">
                    {getText('msg_971d6ded4edf')} {searchCriteria?.uniqueId?.length}{getText('msg_2fd301945bd1')}
                  </small>
                </div>

                <DepartmentDropdown
                  inlineLabel={false}
                  className={
                    "col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2"
                  }
                  selectedDepartment={searchCriteria.departmentIds}
                  onDepartmentChange={(value) =>
                    handleDropdownChange(value, "departmentIds")
                  }
                />

                <LocationDropdown
                  inlineLabel={false}
                  className={
                    "col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2"
                  }
                  selectedLocation={searchCriteria.locationIds}
                  onLocationChange={(value) =>
                    handleDropdownChange(value, "locationIds")
                  }
                />

                <RegionDropdown
                  inlineLabel={false}
                  className={
                    "col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2"
                  }
                  selectedRegion={searchCriteria.regionIds}
                  onRegionChange={(value) =>
                    handleDropdownChange(value, "regionIds")
                  }
                />
                <PayGroupDropdown
                  inlineLabel={false}
                  className={
                    "col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2"
                  }
                  selectedPayGroup={searchCriteria.payGroupIds}
                  onPayGroupChange={(value) =>
                    handleDropdownChange(value, "payGroupIds")
                  }
                />
                <BusinessUnitDropdown
                  inlineLabel={false}
                  className={
                    "col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2"
                  }
                  selectedBusinessUnit={searchCriteria.businessUnitIds}
                  onBusinessUnitChange={(value) =>
                    handleDropdownChange(value, "businessUnitIds")
                  }
                />
                <DivisionDropdown
                  inlineLabel={false}
                  className={
                    "col-12 col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 pe-md-2 pe-lg-2"
                  }
                  selectedDivision={searchCriteria.divisionIds}
                  onDivisionChange={(value) =>
                    handleDropdownChange(value, "divisionIds")
                  }
                />
              </>
            )}

            <button
              onClick={handleSearch}
              type="button"
              className="btn btn-primary me-4  button-base ing-button-primary-left align-middle m-auto"
            >
              <i className="bi bi-filter me-1" />
              {getText('msg_aa3dea27391b')}
            </button>
          </section>

          <div className="table-responsive-xxl w-100">
            {isSearchSent ? (
              <>
                {isLoading ? (
                  <Spinner width={50} className={styles.loadingSpinner} />
                ) : (
                  <>
                    <table className="table table-hover align-middle ing-table-striped">
                      <thead className="custom-table-header table-light">
                        <tr>
                          <th className={`text-center checkbox-column-head`}>
                            <input
                              type="checkbox"
                              ref={handleSelectAllRef}
                              className={`form-check-input`}
                              checked={
                                selectedUsers.length === potentialUsers.length
                              }
                              onChange={(e) =>
                                handleSelectAllChange(e.target.checked)
                              }
                            />
                          </th>
                          <th onClick={() => handleSort("lastName")}>
                            {getText('msg_863cb39fbe7d')} {renderSortArrow("lastName")}
                          </th>
                          <th onClick={() => handleSort("firstName")}>
                            {getText('msg_b6ea992aab46')} {renderSortArrow("firstName")}
                          </th>

                          {user.jwt?.ind != null ? (
                            <>
                              <th onClick={() => handleSort("uniqueId")}>
                                {getText('msg_7aba334bb9bf')} {renderSortArrow("uniqueId")}
                              </th>
                              <th onClick={() => handleSort("department")}>
                                {getText('msg_db40106a4051')} {renderSortArrow("department")}
                              </th>
                              <th onClick={() => handleSort("location")}>
                                {getText('msg_d219c68101f5')} {renderSortArrow("location")}
                              </th>
                              <th onClick={() => handleSort("region")}>
                                {getText('msg_0f217179940c')}
                                {renderSortArrow("region")}
                              </th>
                            </>
                          ) : (
                            <>
                              <th onClick={() => handleSort("emailAddress")}>
                                {getText('msg_84add5b29527')}
                                {renderSortArrow("emailAddress")}
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedUsers.map((user) => (
                          <tr key={user.userId}>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedUsers.includes(user.userId)}
                                onChange={() =>
                                  handleUserSelectionChange(user.userId)
                                }
                              />
                            </td>
                            <td>{user.lastName}</td>
                            <td>{user.firstName}</td>
                            {user.jwt?.ind != null ? (
                              <>
                                <td>{user.uniqueId}</td>
                                <td>{user.department}</td>
                                <td>{user.location}</td>
                                <td>{user.region}</td>
                              </>
                            ) : (
                              <>
                                <td>{user.emailAddress}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            ) : (
              <p className="text-center">
                {getText('msg_9a598da1a7c9')}
              </p>
            )}

            {/* <button onClick={handleCancel}>Cancel</button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserToRole;
